import Input from "components/EvtKit/input/Input"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"
import { memo, useCallback } from "react"
import styled from "styled-components"
import { Colors } from "themes/palette"
import { validateEmail } from "utils/auth/authHelper"
import UploadImageAsFileOrURL from "../components/UploadImageAsFileOrURL"
import Heading from "components/Typography/Heading"
import Button from "components/EvtKit/button/Button"
import { useModal } from "hooks/useModal"
import IconStars from "components/EvtKit/icons/IconStars"
import FederatedKeyInput from "./FederatedKeyInput"
import generateRandomKey from "utils/generateRandomKey"

const Container = styled.div`
  margin-top: 24px;
`

const ContainerInputs = styled.div`
  display: flex;
  gap: 48px;
  justify-items: left;
  align-items: flex-start;
  margin: 8px auto;
  margin-top: 28px;
`
const ContainerInput = styled.div`
  display: grid;
  gap: 44px;
  width: 100%;

  &.ordering-line {
    display: grid;
    gap: 44px;
    grid-template-columns: 1fr 1fr;
    margin-top: 44px;
  }
`
const ContainerSecondInputs = styled.div`
  display: grid;
  gap: 12px;
  width: 100%;
`
const SwitchContainer = styled.div`
  padding: 8px;
  min-width: 195px;
  box-shadow: 0px 0px 4px 0px ${(props) => props.theme.palette.dark_box_shadow};
  background-color: ${(props) => props.theme.palette.gray_ice};
  border-radius: 4px;
`

const ContainerSwitchField = styled.div`
  display: flex;
  gap: 48px;
  width: 100%;
  margin-top: 24px;
  align-items: center;
`

const SizeContainer = styled.div`
  width: 100%;
`
const DescriptionContainer = styled.div`
  margin-top: 24px;
`

const BannerButtonContainer = styled.div`
  width: 300px;
`

const CustomButton = styled(Button)`
  border-radius: 8px;
`

interface Props {
  headingText: string
  productInfo: App.ProductInfo
  setProductInfo: React.Dispatch<React.SetStateAction<App.ProductInfo>>
  setProductName: React.Dispatch<React.SetStateAction<string>>
  productName: string
  productPrefixUrl: string
  setProductPrefixUrl: React.Dispatch<React.SetStateAction<string>>
  logoFile: File | undefined
  setFiles: React.Dispatch<React.SetStateAction<App.ProductFilesObj>>
  emailError: string
  setEmailError: React.Dispatch<React.SetStateAction<string>>
  descriptionError: string
  setDescriptionError: React.Dispatch<React.SetStateAction<string>>
  federatedKey: string
  setFederatedKey: React.Dispatch<React.SetStateAction<string>>
  isUpdate?: boolean
  image: File | undefined
}

const ProductForm = (props: Props) => {
  const {
    headingText,
    productName,
    productPrefixUrl,
    setProductPrefixUrl,
    productInfo,
    logoFile,
    image,
    setProductInfo,
    setProductName,
    setFiles,
    emailError,
    setEmailError,
    descriptionError,
    setDescriptionError,
    federatedKey,
    setFederatedKey,
    isUpdate = false
  } = props

  const { openModal } = useModal()

  const handleOnClick = useCallback(async () => {
    openModal("addProperty")
  }, [openModal])

  const handleGenerateValue = useCallback(() => {
    const generatedValue = generateRandomKey(24)

    setFederatedKey(generatedValue)
  }, [setFederatedKey])

  const handleProductInfoInputsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.target

      if (id === "supportEmail" && value.length > 0 && !validateEmail(value)) {
        setEmailError("E-mail inválido")
      } else {
        setEmailError("")
      }
      if (id === "description") {
        if (value.length > 300) {
          setDescriptionError("A descrição não pode ter mais de 300 caracteres")
        } else {
          setDescriptionError("")
        }
      }
      if (id === "name" && setProductName) {
        setProductName(value)
      }
      if (id === "prefixUrl" && setProductPrefixUrl) {
        setProductPrefixUrl(value)
      }
      if (id === "federatedKey" && setFederatedKey) {
        setFederatedKey(value)
      }
      setProductInfo((prevProductInfo) => ({
        ...prevProductInfo,
        [id]: value
      }))
    },
    [setProductInfo, setProductName, setEmailError, setProductPrefixUrl]
  )

  return (
    <Container>
      <Heading variant="h6">{headingText}</Heading>
      <ContainerInputs>
        <ContainerInput>
          <Input
            type="text"
            id="name"
            name="name"
            label="Nome do Produto *"
            value={productName}
            className="fullWidth"
            required={false}
            onChange={handleProductInfoInputsChange}
            placeholder="Digite"
            shrink
          />
          <UploadImageAsFileOrURL
            id="logoUrl"
            label="URL Logo na Sidebar *"
            inputValue={productInfo.logoUrl}
            name="logoUrl"
            file={logoFile}
            setFiles={setFiles}
            handleInputChange={handleProductInfoInputsChange}
            acceptFormats={["image/png"]}
            placeholder="Digite ou faça upload (png)"
          />
        </ContainerInput>
        <ContainerSecondInputs>
          <Input
            id="prefixUrl"
            type="text"
            name="prefixUrl"
            label="Prefixo da URL *"
            className="fullWidth"
            value={productPrefixUrl}
            informativeMessage="Este campo não poderá ser alterado após a criação do produto."
            color="gray-medium"
            colorIcon={Colors.primary_one}
            disabled={isUpdate}
            onChange={handleProductInfoInputsChange}
            placeholder="Digite"
            shrink
          />
          <Input
            id="supportEmail"
            type="text"
            name="supportEmail"
            label="E-mail de suporte *"
            value={productInfo.supportEmail}
            className="fullWidth"
            onChange={handleProductInfoInputsChange}
            error={emailError}
            required={false}
            placeholder="Digite"
            shrink
          />
        </ContainerSecondInputs>
      </ContainerInputs>
      <ContainerInput className="ordering-line">
        <UploadImageAsFileOrURL
          id="image"
          name="image"
          label="URL Icone do Produto *"
          handleInputChange={handleProductInfoInputsChange}
          acceptFormats={["image/png", "image/webp"]}
          placeholder="Digite ou faça upload (png/webp)"
          inputValue={productInfo.image}
          file={image}
          setFiles={setFiles}
        />
        <Input
          id="productLandingPageURL"
          type="text"
          name="productLandingPageURL"
          label="URL Landing Page do Produto"
          value={productInfo.productLandingPageURL}
          className="fullWidth"
          onChange={handleProductInfoInputsChange}
          placeholder="Digite"
          shrink
        />
      </ContainerInput>
      <ContainerSwitchField>
        <SwitchContainer>
          <SwitchV2 label="Produto disponível para afiliação" variant="medium" disabled={true} />
        </SwitchContainer>
        <FederatedKeyInput
          inputValue={federatedKey}
          onClick={handleGenerateValue}
          onChange={handleProductInfoInputsChange}
        />
        <BannerButtonContainer>
          <CustomButton
            label={"Identidade visual"}
            onClick={handleOnClick}
            iconLeft={<IconStars />}
            className="fill-width"
          />
        </BannerButtonContainer>
      </ContainerSwitchField>
      <DescriptionContainer>
        <SizeContainer>
          <Input
            id="description"
            type="text"
            name="description"
            label="Descrição *"
            shrink
            placeholder="Digite"
            value={productInfo.description}
            className="fullWidth"
            onChange={handleProductInfoInputsChange}
            informativeMessage="Apresente seu produto de forma concisa: destaque as principais características em uma descrição breve e direta, garantindo a compreensão do cliente."
            color="informative"
            colorIcon={Colors.informative}
            informativeMessagePosition="top"
            error={descriptionError}
            safeBottom
            safeBottomInformative
            limitCharacters={300}
          />
        </SizeContainer>
      </DescriptionContainer>
    </Container>
  )
}

export default memo(ProductForm)
